import { supabase } from '@/supabase';
import { handleServiceError, createServiceResponse } from '@/helpers/errorHandling';
import { t } from '@/i18n';
import AppMessages from '@/models/core/AppMessages';
import type { CreateUserRequest, CreateUserResponse } from '@/types/user';
import ServiceDomains from '@/constants/ServiceDomains';

/**
 * Creates or retrieves a user by email.
 * Validates that the email belongs to an admin employee.
 * If the user doesn't exist in auth.users, creates a new user with a random password.
 * Returns the user UID.
 */
export async function createUser(email: string): Promise<{
  data: CreateUserResponse | null;
  error: string | null;
}> {
  try {
    if (!email) {
      const errorMessage = t('errors.createUser.EMAIL_REQUIRED');
      AppMessages.error(errorMessage);
      return {
        data: null,
        error: errorMessage,
      };
    }

    const { data, error } = await supabase.functions.invoke('create-user', {
      body: { email } as CreateUserRequest,
    });

    if (error) {
      console.error('Error creating user:', error);
      return createServiceResponse(null, error, ServiceDomains.CreateUser);
    }

    return {
      data: data as CreateUserResponse,
      error: null,
    };
  } catch (error: unknown) {
    console.error('Unexpected error creating user:', error);
    const errorMessage = t('errors.createUser.INTERNAL_SERVER_ERROR');
    AppMessages.error(errorMessage);
    return {
      data: null,
      error: errorMessage,
    };
  }
}
